// Dependencies
import axios from 'axios'

// Services
import { accessHeaders } from "../../lib/cookie"

// Utils
import {
  ConvertUtility as Convert
} from '../utils/convert-utility'

import {
  ClientUtility as Client
} from '../utils/client-utility'

/**
 * Role filter parameters
 * @typedef RoleFilter
 * @type {Object}
 * @property {String} text      - Text search
 * @property {String} homeId    - Home ID
 * @property {String} projectId - Project ID
 * @property {String} role      - OWNER, RESIDENT, or RENTER
 * @property {String} mobileNo  - Mobile number
 * @property {Number} page      - Fetch page
 * @property {Number} limit     - Fetch Limit
 */

/**
 * Role user properties
 * @typedef RoleUser
 * @type {Object}
 * @property {Number} id        - User ID
 * @property {String} firstName - First name
 * @property {String} lastName  - Last name
 * @property {String} mobileNo  - Mobile number
 * @property {String} [email]   - Email
 */

/**
 * Role home properties
 * @typedef RoleHome
 * @type {Object}
 * @property {Number} id        - Home ID
 * @property {String} addressNo - Address number
 */

/**
 * Role project properties
 * @typedef RoleProject
 * @type {Object}
 * @property {String} id   - Project ID
 * @property {String} name - Project name
 * @property {String} code - Project code
 * @property {String} icon - Project icon image URL
 */

/**
 * Role item properties
 * @typedef RoleItem
 * @type {Object}
 * @property {Number} id            - Role ID
 * @property {String} role          - OWNER, RESIDENT, or RENTER
 * @property {RoleUser} user        - User details
 * @property {RoleHome} home        - Home details
 * @property {RoleProject} project  - Project details
 * @property {Date} createdAt       - Creation time
 */

/**
 * Role create parameters
 * @typedef RoleCreate
 * @type {Object}
 * @property {String} mobileNo  - Mobile Number
 * @property {String} homeId    - Home ID
 * @property {String} projectId - Project ID
 * @property {String} role      - OWNER, RESIDENT, or RENTER
 */

/**
 * Role list properties
 * @typedef RoleList
 * @type {Object}
 * @property {RoleItem[]} items - Role list
 * @property {Number} total     - Total result
 */

/**
 * Role creation result properties
 * @typedef CreateResult
 * @type {Object}
 * @property {String} id        - Unique ID
 * @property {String} createdAt - Creation time
 */

/**
 * Role deletion result properties
 * @typedef DeleteResult
 * @type {Object}
 * @property {Boolean} deleted - Delete state
 */

export class NonScUserRoleClient {

  // Request Endpoint
  static endpoint = (
    `${process.env.ADMIN_HOST}/ruejai-backend-admin/api/user/nonsc/role`
  )

  /**
   * Fetch role list
   * @param {RoleFilter} parmas - Filter Parameters
   * @returns {Promise<RoleList>} Role list
   */
  static async list(params = {}) {

    // Request URL
    const url = `${this.endpoint}/list`
    
    // Default Result
    let result = {
      items: [], total: 0
    }

    try {
      
      // Request Headers
      const headers = accessHeaders()

      // Request Project List
      const { data } = await axios.get(
        url, { headers, params }
      )

      // Validate Result
      if (Client.has(data)) {
        result = Convert.key(data.result)
      }
    }
    catch (error) {
      
      // Error Message
      const { message } = error

      // Error Response
      const { errorCode } = Client.error(
        'nonsc-user-role.list', error
      )

      // Execute Error 
      throw new Error(
        errorCode || message
      )
    }

    return result
  }

  /**
   * Request create role
   * @param {RoleCreate} payload - Create role parameters
   * @returns {Promise<CreateResult>} Create result
   */
  static async create(payload) {
    
    // Request URL
    const url = this.endpoint
    
    // Default Result
    let result = null
    
    try {
      
      // Request Headers
      const headers = accessHeaders()

      // Request Create Role
      const { data } = await axios.post(
        url, payload, { headers }
      )

      // Validate Result
      if (Client.has(data)) {
        result = data.result
      }
    }
    catch (error) {
      
      // Error Message
      const { message } = error

      // Error Response
      const { errorCode } = Client.error(
        'nonsc-user-role.create', error
      )

      // Execute Error 
      throw new Error(
        errorCode || message
      )
    }

    return result
  }

  /**
   * Request remove role
   * @param {Number} roleId - Role ID 
   * @returns {Promise<DeleteResult>} Remove result
   */
  static async remove(roleId) {
    
    // Request URL
    const url = `${this.endpoint}/${roleId}`
    
    // Default Result
    let result = null

    try {
      
      // Request Headers
      const headers = accessHeaders()

      // Request Remove Role
      const { data } = await axios.delete(
        url, { headers }
      )

      // Validate Result
      if (Client.has(data)) {
        result = data.result
      }
    }
    catch (error) {

      // Error Message
      const { message } = error

      // Error Response
      const { errorCode } = Client.error(
        'nonsc-user-role.remove', error
      )

      // Execute Error 
      throw new Error(
        errorCode || message
      )
    }

    return result
  }
}


// WEBPACK FOOTER //
// ./src/clients/BackendAdminClient/nonsc-user-role.js